import { useState, useRef, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Idea } from '../../types';
import { voteOnIdea } from '../../services/supabase/content';
import { saveIdea, unsaveIdea } from '../../services/supabase/user';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/use-toast';
import { 
  BookmarkIcon, 
  Share2, 
  ThumbsUp, 
  ThumbsDown,
  ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn, slugify } from '../../lib/utils';
import { savePathAndRedirectToLogin } from '../../lib/auth';

interface IdeaCardProps {
  idea: Idea;
  onSaveStateChange?: (ideaId: string, isSaved: boolean) => void;
}

const MAX_SHORT_DESC_LENGTH = 150;
const MAX_DETAILED_DESC_LENGTH = 300;

/**
 * Formats a number with commas as thousands separators
 * @param num - The number to format
 * @returns Formatted number with commas
 */
const formatNumberWithCommas = (num: number | string | null | undefined): string => {
  if (num === null || num === undefined) {
    return "0";
  }
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * Formats the market size based on the currency parameter
 * @param marketSize - The market size value
 * @param currency - The currency string (optional)
 * @returns Formatted market size with appropriate currency symbol and suffix
 */
const formatMarketSize = (marketSize: number | null | undefined, currency?: string): string => {
  if (marketSize === null || marketSize === undefined) {
    marketSize = 0;
  }
  
  // Round the market size to remove decimal points
  marketSize = Math.round(marketSize);
  
  if (!currency) {
    // Default to USD if no currency is provided
    return `$${formatNumberWithCommas(marketSize)}`;
  }

  const currencyLower = currency.toLowerCase();
  
  if (currencyLower.includes('inr')) {
    // For Indian Rupee, convert to crores (1 crore = 10 million)
    const croreValue = marketSize / 10;
    // Round to the nearest whole number
    return `₹${formatNumberWithCommas(Math.round(croreValue))} crore`;
  } else if (currencyLower.includes('usd')) {
    // For US Dollar
    return `$${formatNumberWithCommas(marketSize)}`;
  } else {
    // For any other currency, use the first 3 characters as the symbol
    // and "M" as the suffix
    const symbol = currency.substring(0, 3).toUpperCase();
    return `${symbol} ${formatNumberWithCommas(marketSize)}`;
  }
};

/**
 * Parses markdown-style formatting (**bold**) in text and converts to JSX
 * @param text - The text containing markdown-style formatting
 * @returns Array of React nodes with proper formatting
 */
const parseMarkdownStyle = (text: string): ReactNode[] => {
  if (!text) return [text];

  // First, split by newlines to handle paragraphs
  const paragraphs = text.split('\n\n');
  
  return paragraphs.flatMap((paragraph, pIndex) => {
    // Check if this paragraph is a bullet point list
    if (paragraph.trim().startsWith('*')) {
      // Process bullet points
      const items = paragraph.split('\n*').map(item => 
        item.startsWith('*') ? item.substring(1).trim() : item.trim()
      );
      
      return [
        <div key={`p-${pIndex}`} className="mb-4">
          <ul className="list-disc pl-5 space-y-1">
            {items.map((item, i) => (
              <li key={`li-${pIndex}-${i}`}>
                {processBoldText(item)}
              </li>
            ))}
          </ul>
        </div>
      ];
    } else {
      // Process normal paragraphs
      return [
        <div key={`p-${pIndex}`} className="mb-4">
          {processBoldText(paragraph)}
        </div>
      ];
    }
  });
};

/**
 * Process bold text marked with ** within a text fragment
 * @param text - Text that may contain ** markers
 * @returns Array of text and bold elements
 */
const processBoldText = (text: string): ReactNode[] => {
  // Split the text by ** markers
  const parts = text.split(/(\*\*.*?\*\*)/g);
  
  return parts.map((part, index) => {
    // Check if this part is wrapped in ** markers
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove the ** markers and make the text bold
      const content = part.substring(2, part.length - 2);
      return <strong key={index}>{content}</strong>;
    }
    // Return regular text
    return part;
  });
};

export const IdeaCard = ({ idea: initialIdea, onSaveStateChange }: IdeaCardProps) => {
  const [idea, setIdea] = useState(initialIdea);
  const [isVoting, setIsVoting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showFullShortDesc, setShowFullShortDesc] = useState(false);
  const [showFullDetailedDesc, setShowFullDetailedDesc] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  // Add a ref to track if a save request is in progress
  const saveRequestInProgress = useRef(false);
  const voteRequestInProgress = useRef(false);
  const previousIdeaId = useRef(initialIdea.id);

  // Update local state when the parent component passes a new idea prop
  useEffect(() => {

    
    // Don't update from parent if we have a vote request in progress
    if (voteRequestInProgress.current) {
    
      return;
    }
    
    // Update if the idea ID has changed OR if the vote state has changed
    if (previousIdeaId.current !== initialIdea.id || 
        idea.user_vote !== initialIdea.user_vote ||
        idea.upvotes !== initialIdea.upvotes ||
        idea.downvotes !== initialIdea.downvotes) {
     
      setIdea(initialIdea);
      previousIdeaId.current = initialIdea.id;
    }
  }, [initialIdea.id, initialIdea.user_vote, initialIdea.upvotes, initialIdea.downvotes]); // Add vote-related dependencies

  // Debug log to track render state
  useEffect(() => {
   
  });

  // Add additional logging for button color determination
  const getUpvoteButtonColor = () => {
    const isUpvoted = idea.user_vote === 'upvote';
    const colorClass = isVoting ? 'opacity-50 cursor-not-allowed' : 
      isUpvoted ? 'text-green-600 hover:text-green-700' : 'text-gray-600 hover:text-green-600';
    
   
    
    return colorClass;
  };

  const getDownvoteButtonColor = () => {
    const isDownvoted = idea.user_vote === 'downvote';
    const colorClass = isVoting ? 'opacity-50 cursor-not-allowed' : 
      isDownvoted ? 'text-red-600 hover:text-red-700' : 'text-gray-600 hover:text-red-600';
    
   
    
    return colorClass;
  };

  const handleVote = async (type: 'upvote' | 'downvote') => {
  
    
    // Prevent duplicate vote requests
    if (voteRequestInProgress.current) {
      return;
    }
    
    if (isVoting) return;
    
    if (!user) {
      // Use the proper auth redirection function
      const currentPath = window.location.pathname + window.location.search;
      savePathAndRedirectToLogin(currentPath);
      return;
    }
    
    setIsVoting(true);
    voteRequestInProgress.current = true;


    try {
      const updatedIdea = await voteOnIdea(idea.id, type);

      setIdea(updatedIdea);
    } catch (error) {
      console.error('Failed to update vote:', error);
      toast({
        title: 'Error',
        description: 'Failed to update vote. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsVoting(false);
      // Reset the vote request lock after a short delay to prevent immediate retries
      setTimeout(() => {
        voteRequestInProgress.current = false;
      }, 500);
    }
  };

  const handleSave = async () => {
    // Prevent duplicate save requests
    if (saveRequestInProgress.current) {
      console.log('Save request already in progress, ignoring duplicate request');
      return;
    }

    if (!user) {
      // Use the proper auth redirection function
      const currentPath = window.location.pathname + window.location.search;
      savePathAndRedirectToLogin(currentPath);
      return;
    }

    if (isSaving) return;

    setIsSaving(true);
    saveRequestInProgress.current = true;
    
    try {
      if (idea.is_saved) {
        await unsaveIdea(idea.id);
        setIdea(prev => ({ ...prev, is_saved: false }));
        // Notify parent component about the state change
        onSaveStateChange?.(idea.id, false);
      } else {
        await saveIdea(idea.id);
        setIdea(prev => ({ ...prev, is_saved: true }));
        // Notify parent component about the state change
        onSaveStateChange?.(idea.id, true);
        
        // Show the save success animation
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 1500);
      }
    } catch (error) {
      console.error('Failed to save idea:', error);
      toast({
        title: 'Error',
        description: 'Failed to save idea. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
      // Reset the request lock after a short delay to prevent immediate retries
      setTimeout(() => {
        saveRequestInProgress.current = false;
      }, 500);
    }
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent navigation if a button, link or text selection is clicked
    if (
      (e.target as HTMLElement).closest('button') ||
      (e.target as HTMLElement).closest('a') ||
      window.getSelection()?.toString()
    ) {
      return;
    }
    navigate(`/ideas/${idea.id}-${slugify(idea.title)}`);
  };

  const handleShare = async () => {
    if (!idea) return;

    const ideaUrl = `${window.location.origin}/ideas/${idea.id}-${slugify(idea.title)}`;

    // Use Web Share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: idea.title,
          text: idea.short_description,
          url: ideaUrl,
        });
      } catch (error) {
        // If navigator.share fails (e.g., user cancels, or API error),
        // or if we want to always offer a copy option alongside native share,
        // we can fall back to clipboard copy.
        // For this implementation, we'll fall back if sharing fails.
        console.error('Error using Web Share API:', error);
        try {
          await navigator.clipboard.writeText(ideaUrl);
          toast({
            title: 'Link Copied!',
            description: 'Idea URL copied to clipboard.',
          });
        } catch (copyError) {
          console.error('Failed to copy link to clipboard:', copyError);
          toast({
            title: 'Error',
            description: 'Could not copy link. Please try again.',
            variant: 'destructive',
          });
        }
      }
    } else {
      // Fallback for browsers that do not support Web Share API
      try {
        await navigator.clipboard.writeText(ideaUrl);
        toast({
          title: 'Link Copied!',
          description: 'Idea URL copied to clipboard.',
        });
      } catch (error) {
        console.error('Failed to copy link to clipboard:', error);
        toast({
          title: 'Error',
          description: 'Could not copy link. Please try again.',
          variant: 'destructive',
        });
      }
    }
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (!text || text.length <= maxLength) return text;

    // Check if there's a paragraph break before the maxLength
    const paragraphBreakIndex = text.indexOf('\n\n', 0);
    if (paragraphBreakIndex > 0 && paragraphBreakIndex < maxLength - 20) {
      // If a paragraph break exists within a reasonable range, truncate there
      return text.slice(0, paragraphBreakIndex) + '...';
    }

    // Find a good breaking point - prefer ending at a sentence
    let truncateIndex = maxLength;
    const lastSentenceBreak = text.lastIndexOf('. ', maxLength - 5);
    
    if (lastSentenceBreak > maxLength * 0.6) {
      // If we found a sentence break within the last 40% of maxLength, use it
      truncateIndex = lastSentenceBreak + 1;
    } else {
      // Otherwise, try to find a space to break at
      const lastSpaceIndex = text.lastIndexOf(' ', maxLength - 3);
      if (lastSpaceIndex > 0) {
        truncateIndex = lastSpaceIndex;
      }
    }

    return text.slice(0, truncateIndex).trim() + '...';
  };

  const renderDescription = (
    text: string,
    maxLength: number,
    isExpanded: boolean,
    onToggle: () => void
  ) => {
    const shouldTruncate = text.length > maxLength;
    const displayText = isExpanded ? text : truncateText(text, maxLength);

    return (
      <div className="relative">
        <div className="text-gray-600">
          {parseMarkdownStyle(displayText)}
        </div>
        {shouldTruncate && (
          <button
            onClick={onToggle}
            className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 transition-all duration-200 hover:shadow-xl">
      {/* Title and Action Buttons */}
      <div className="flex flex-wrap justify-between items-start mb-4 gap-2">
        <div 
          className="cursor-pointer"
          onClick={handleCardClick}
        >
          <a 
            href={`/ideas/${idea.id}-${slugify(idea.title)}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/ideas/${idea.id}-${slugify(idea.title)}`);
            }}
            className="hover:underline"
          >
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex-grow">{idea.title}</h3>
          </a>
        </div>
        <div className="flex gap-2 text-gray-600 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleShare}
            className="h-8 w-8 hover:text-blue-600"
          >
            <Share2 size={20} />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleSave} 
            disabled={isSaving}
            className={cn(
              "relative",
              idea.is_saved ? "text-primary" : "text-muted-foreground hover:text-primary"
            )}
          >
            <BookmarkIcon className={cn(
              "h-5 w-5 transition-all",
              idea.is_saved ? "fill-current" : "",
              showSaveSuccess && !idea.is_saved ? "scale-125 text-green-500" : ""
            )} />
            {showSaveSuccess && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Short Description */}
      <div className="mb-6">
        {renderDescription(
          idea.short_description,
          MAX_SHORT_DESC_LENGTH,
          showFullShortDesc,
          () => setShowFullShortDesc(!showFullShortDesc)
        )}
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">Market Size</div>
          <div className="text-base sm:text-lg font-semibold text-gray-900">
            {idea.normalized_market_size_usd !== undefined && idea.normalized_market_size_usd !== null
              ? `$${formatNumberWithCommas(idea.normalized_market_size_usd)}` 
              : (idea.market_size !== undefined 
                  ? formatMarketSize(idea.market_size, idea.currency)
                  : "$0")}
          </div>
        </div>
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">Timeline</div>
          <div className="text-base sm:text-lg font-semibold text-gray-900">{idea.timeline || "12-18 months"}</div>
        </div>
      </div>

      {/* Detailed Description */}
      <div className="mb-6">
        {renderDescription(
          idea.detailed_description,
          MAX_DETAILED_DESC_LENGTH,
          showFullDetailedDesc,
          () => setShowFullDetailedDesc(!showFullDetailedDesc)
        )}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-6">
        {idea.business_area.split(',').map((area, index) => (
          <span 
            key={index}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              area.trim().toLowerCase() === 'fintech' 
                ? 'bg-blue-50 text-blue-600'
                : area.trim().toLowerCase() === 'healthcare'
                ? 'bg-green-50 text-green-600'
                : 'bg-purple-50 text-purple-600'
            }`}
          >
            {area.trim()}
          </span>
        ))}
      </div>

      {/* Bottom Section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Voting Controls */}
        <div className="flex items-center space-x-6">
          <button
            key={`upvote-${idea.user_vote}-${isVoting}`}
            onClick={() => handleVote('upvote')}
            disabled={isVoting}
            className={`flex items-center space-x-2 transition-colors ${getUpvoteButtonColor()}`}
          >
            <ThumbsUp size={20} />
            <span className="font-medium">{idea.upvotes}</span>
          </button>

          <button
            key={`downvote-${idea.user_vote}-${isVoting}`}
            onClick={() => handleVote('downvote')}
            disabled={isVoting}
            className={`flex items-center space-x-2 transition-colors ${getDownvoteButtonColor()}`}
          >
            <ThumbsDown size={20} />
            <span className="font-medium">{idea.downvotes}</span>
          </button>
        </div>

        {/* Explore Business Plan Link */}
        <button 
          className="flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
          onClick={() => navigate(`/ideas/${idea.id}`)}
        >
          Explore business plan
          <ChevronRight size={20} className="ml-1" />
        </button>
      </div>
    </div>
  );
}; 
